import { useState, useCallback } from "react";
import { useQuery } from "react-query";
import { recruitingAPI } from "../../services/recruitingApi";
import type { UserRole } from "../../types/shared.types";

export interface NotificationState {
  open: boolean;
  message: string;
  severity: "success" | "error";
}

export interface ConfirmDialogState {
  open: boolean;
  personnelId: number | null;
  label: string;
}

export interface AssignedPerson {
  id: number;
  [key: string]: unknown;
}

export interface AvailablePerson {
  id: number;
  username: string;
  email?: string;
}

interface AssignedResponse<T> {
  success: boolean;
  data?: Record<string, T[]>;
}

interface UsersByRoleResponse {
  success: boolean;
  users?: AvailablePerson[];
}

interface PersonnelAssignmentConfig<T extends AssignedPerson> {
  /** Role slug for fetching available personnel (e.g., "recruiter", "tech_checker") */
  roleSlug: string;
  /** React Query key prefix for assigned list (e.g., "candidate-recruiters") */
  assignedQueryKey: string;
  /** React Query key for available personnel list (e.g., "recruiters-list") */
  availableQueryKey: string;
  /** Key in response.data that holds the assigned array (e.g., "assigned_recruiters") */
  assignedDataKey: string;
  /** API function to fetch assigned personnel */
  fetchAssigned: (candidateId: number) => Promise<unknown>;
  /** API function to assign personnel */
  assign: (candidateId: number, personnelId: number) => Promise<unknown>;
  /** API function to remove personnel */
  remove: (candidateId: number, personnelId: number) => Promise<unknown>;
  /** Label for success/error messages (e.g., "Recruiter", "Tech checker") */
  label: string;
  /** Whether the assigned list query should be gated on canManage (tech checkers: true, recruiters: false) */
  gateAssignedOnPermission?: boolean;
  /** Extract the personnel ID from an assigned record (e.g., r => r.recruiter_id) */
  getPersonnelId: (assigned: T) => number;
}

export interface UsePersonnelAssignmentOptions {
  candidateId: number | string;
  userRole: string;
  enabled?: boolean;
}

export interface UsePersonnelAssignmentReturn<T extends AssignedPerson> {
  assigned: T[];
  available: AvailablePerson[];
  selectedId: string;
  setSelectedId: (id: string) => void;
  isAssigning: boolean;
  handleAssign: (overrideId?: string) => Promise<void>;
  handleRemove: (personnelId: number) => void;
  canManage: boolean;
  notification: NotificationState;
  closeNotification: () => void;
  confirmDialog: ConfirmDialogState;
  confirmRemoval: () => void;
  cancelRemoval: () => void;
}

const CAN_MANAGE_ROLES = [
  "admin",
  "application_admin",
  "organization_admin",
  "head_of_recruitment",
  "head_of_sales",
  "recruiter",
  "sales",
];

export function createPersonnelAssignmentHook<T extends AssignedPerson>(
  config: PersonnelAssignmentConfig<T>,
) {
  return function usePersonnelAssignment({
    candidateId,
    userRole,
    enabled = true,
  }: UsePersonnelAssignmentOptions): UsePersonnelAssignmentReturn<T> {
    const [assigned, setAssigned] = useState<T[]>([]);
    const [selectedId, setSelectedId] = useState("");
    const [isAssigning, setIsAssigning] = useState(false);
    const [available, setAvailable] = useState<AvailablePerson[]>([]);
    const [notification, setNotification] = useState<NotificationState>({
      open: false,
      message: "",
      severity: "success",
    });
    const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState>({
      open: false,
      personnelId: null,
      label: config.label,
    });

    const numericId =
      typeof candidateId === "string"
        ? Number.parseInt(candidateId, 10)
        : candidateId;
    const canManage = CAN_MANAGE_ROLES.includes(userRole);

    const assignedEnabled = config.gateAssignedOnPermission
      ? enabled && !!candidateId && canManage
      : enabled && !!candidateId;

    const { refetch } = useQuery<AssignedResponse<T>>(
      [config.assignedQueryKey, candidateId],
      () =>
        config.fetchAssigned(numericId) as unknown as Promise<
          AssignedResponse<T>
        >,
      {
        enabled: assignedEnabled,
        onSuccess: (data) => {
          if (data?.success && data.data) {
            setAssigned(data.data[config.assignedDataKey] || []);
          }
        },
      },
    );

    useQuery<UsersByRoleResponse>(
      config.availableQueryKey,
      () =>
        recruitingAPI.getUsersByRole(
          config.roleSlug as UserRole,
        ) as unknown as Promise<UsersByRoleResponse>,
      {
        enabled: enabled && canManage && !!candidateId,
        staleTime: 5 * 60 * 1000,
        onSuccess: (data) => {
          if (data?.success && data.users) {
            setAvailable(data.users);
          }
        },
      },
    );

    const showNotification = useCallback(
      (message: string, severity: "success" | "error") => {
        setNotification({ open: true, message, severity });
      },
      [],
    );

    const closeNotification = useCallback(() => {
      setNotification((prev) => ({ ...prev, open: false }));
    }, []);

    const handleAssign = async (overrideId?: string) => {
      const idToUse = overrideId || selectedId;
      if (!idToUse) return;

      setIsAssigning(true);
      try {
        await config.assign(numericId, Number.parseInt(idToUse, 10));
        setSelectedId("");
        refetch();
        showNotification(`${config.label} assigned successfully!`, "success");
      } catch (error) {
        const err = error as {
          response?: {
            data?: { error?: string; message?: string; detail?: string };
          };
          message?: string;
        };
        const detail = err.response?.data?.detail
          ? ` (${err.response.data.detail})`
          : "";
        showNotification(
          `Error assigning ${config.label.toLowerCase()}: ` +
            (err.response?.data?.error ||
              err.response?.data?.message ||
              err.message) +
            detail,
          "error",
        );
      } finally {
        setIsAssigning(false);
      }
    };

    const handleRemove = (personnelId: number) => {
      setConfirmDialog({
        open: true,
        personnelId,
        label: config.label,
      });
    };

    const confirmRemoval = useCallback(async () => {
      const personnelId = confirmDialog.personnelId;
      setConfirmDialog((prev) => ({ ...prev, open: false }));
      if (personnelId === null) return;

      try {
        await config.remove(numericId, personnelId);
        refetch();
        showNotification(
          `${config.label} assignment removed successfully!`,
          "success",
        );
      } catch (error) {
        const err = error as {
          response?: { data?: { error?: string; message?: string } };
          message?: string;
        };
        showNotification(
          `Error removing ${config.label.toLowerCase()}: ` +
            (err.response?.data?.error ||
              err.response?.data?.message ||
              err.message),
          "error",
        );
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [confirmDialog.personnelId, numericId]);

    const cancelRemoval = useCallback(() => {
      setConfirmDialog((prev) => ({ ...prev, open: false }));
    }, []);

    return {
      assigned,
      available,
      selectedId,
      setSelectedId,
      isAssigning,
      handleAssign,
      handleRemove,
      canManage,
      notification,
      closeNotification,
      confirmDialog,
      confirmRemoval,
      cancelRemoval,
    };
  };
}
